import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Divider from '@mui/material/Divider';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { catalogosService } from '../../services/catalogosService';
import { Linea, Ramo } from '../../types';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

const Lineas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRamo, setSelectedRamo] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout>();
  const [openImportar, setOpenImportar] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estructura de plantilla para Líneas (según API /api/Catalogos/lineas)
  const columnasLinea = [
    'codigoLinea',
    'nombreLinea',
    'estadoLinea',
    'codigoRamo',
    'articuloXlistaPrecios'
  ];
  const camposObligatoriosLinea = ['codigoLinea','nombreLinea','estadoLinea'];

  const loadRamos = useCallback(async () => {
    try {
      const response = await catalogosService.getRamos(1, 1000);
      if (response && response.items) {
        setRamos(response.items);
      } else {
        setRamos([]);
        enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar ramos:', error);
      setRamos([]);
      enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadLineas = useCallback(async (
    pageNumber: number, 
    pageSize: number, 
    search: string, 
    codigoRamo: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await catalogosService.getLineas(pageNumber + 1, pageSize, search, codigoRamo);
      console.log('Respuesta de líneas:', response);
      if (response && response.items) {
        setLineas(response.items);
        setTotalCount(response.totalCount);
      } else {
        setLineas([]);
        setTotalCount(0);
        setError('No se pudo cargar la información de líneas');
      }
    } catch (error) {
      console.error('Error al cargar líneas:', error);
      setLineas([]);
      setTotalCount(0);
      setError('Error al cargar las líneas');
      enqueueSnackbar('Error al cargar las líneas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadRamos();
  }, [loadRamos]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadLineas(page, rowsPerPage, searchTerm, selectedRamo);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, selectedRamo, loadLineas]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeoutId = setTimeout(() => {
      setPage(0);
      loadLineas(0, rowsPerPage, value, selectedRamo);
    }, 500);

    setSearchTimeout(timeoutId);
  };

  const handleRamoChange = (event: SelectChangeEvent) => {
    setSelectedRamo(event.target.value);
    setPage(0);
  };

  const handleDelete = async (codigoLinea: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta línea?')) {
      try {
        await catalogosService.deleteLinea(codigoLinea);
        enqueueSnackbar('Línea eliminada con éxito', { variant: 'success' });
        loadLineas(page, rowsPerPage, searchTerm, selectedRamo);
      } catch (error) {
        console.error('Error al eliminar línea:', error);
        enqueueSnackbar('Error al eliminar la línea', { variant: 'error' });
      }
    }
  };

  // Nota: navegar se hace inline en el botón; función separada eliminada para evitar warning por no uso.

  const handleDescargarPlantilla = () => {
    try {
      const data = [columnasLinea, new Array(columnasLinea.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Lineas');
      // Hoja de instrucciones con valores aceptados
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['codigoLinea', 'Código único de la línea', 'Texto alfanumérico (obligatorio)'],
        ['nombreLinea', 'Nombre de la línea', 'Texto (obligatorio)'],
        ['estadoLinea', 'Estado de la línea', 'Activo o Inactivo (obligatorio)'],
        ['codigoRamo', 'Ramo al que pertenece', 'Código de ramo existente (opcional)'],
        ['articuloXlistaPrecios', 'Artículos por lista de precio', 'JSON array (opcional, dejar vacío si no aplica)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      XLSX.writeFile(wb, 'PlantillaLineas.xlsx');
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', { variant: 'success', autoHideDuration: 4000 });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      enqueueSnackbar('Archivo leído, procesando filas.', { variant: 'info', autoHideDuration: 3000 });
      if (!rows.length) { enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 }); return; }
      const normalizedRows = rows.map((r) => ({ ...r }));
      const headers = Object.keys(normalizedRows[0]);
      const noEncontrados = columnasLinea.filter(h => !headers.includes(h));
      if (noEncontrados.length > 0) { enqueueSnackbar('Columnas faltantes: ' + noEncontrados.join(', '), { variant: 'error', autoHideDuration: 5000 }); return; }
      // Pre-cargar líneas para evitar duplicados
      const existentes = await catalogosService.getLineas(1, 10000, '', '');
      const existeCodigo = new Set((existentes.items || []).map((l: any) => String(l.codigoLinea).trim()));
      let exitos = 0, fallos = 0;
      for (let i = 0; i < normalizedRows.length; i++) {
        const row = normalizedRows[i];
        // Validar obligatorios
        const faltanCampos: string[] = [];
        for(const campo of camposObligatoriosLinea) { if (!row[campo]) faltanCampos.push(campo); }
        if (faltanCampos.length > 0) { enqueueSnackbar(`❌ Fila ${i + 2}: Falta completar: ${faltanCampos.join(', ')}`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }

        const codigoLinea = String(row.codigoLinea).trim();
        if (existeCodigo.has(codigoLinea)) { enqueueSnackbar(`🔴 Línea duplicada (codigoLinea: ${codigoLinea})`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }

        // Validación de tipos: strings esperados (Excel puede devolver números como number)
        const camposString: string[] = ['codigoLinea','nombreLinea','estadoLinea','codigoRamo'];
        const tiposInvalidos: string[] = [];
        for (const campo of camposString) {
          const valor = (row as any)[campo];
          if (valor === undefined || valor === null || String(valor).trim() === '') continue; // vacíos permitidos si no son obligatorios
          // Aceptar string y number (Excel exporta "13", "001" como número)
          if (typeof valor !== 'string' && typeof valor !== 'number') tiposInvalidos.push(`${campo} (esperado: string)`);
        }
        if (tiposInvalidos.length > 0) {
          enqueueSnackbar(`❌ Fila ${i + 2}: Tipo de dato inválido en ${tiposInvalidos.join(', ')}`, { variant: 'error', autoHideDuration: 9000 });
          fallos++; continue;
        }

        // articuloXlistaPrecios como JSON array opcional
        let articuloXlistaPrecios: any[] | undefined = undefined;
        if (row.articuloXlistaPrecios !== undefined && row.articuloXlistaPrecios !== '') {
          try {
            const parsed = typeof row.articuloXlistaPrecios === 'string' ? JSON.parse(row.articuloXlistaPrecios) : row.articuloXlistaPrecios;
            if (Array.isArray(parsed)) articuloXlistaPrecios = parsed; else { enqueueSnackbar(`❌ Fila ${i + 2}: articuloXlistaPrecios debe ser un JSON de array`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }
          } catch { enqueueSnackbar(`❌ Fila ${i + 2}: articuloXlistaPrecios no es un JSON válido`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }
        }

        // Normalizar estadoLinea a ACTIVO/INACTIVO (case-insensitive)
        const estadoRaw = String(row.estadoLinea).trim();
        const estadoLower = estadoRaw.toLowerCase();
        if (estadoLower !== 'activo' && estadoLower !== 'inactivo') {
          enqueueSnackbar(`❌ Fila ${i + 2}: estadoLinea inválido (use "Activo" o "Inactivo")`, { variant: 'error', autoHideDuration: 8000 });
          fallos++;
          continue;
        }
        const estadoLinea = estadoLower === 'activo' ? 'ACTIVO' : 'INACTIVO';

        const codigoRamo = String(row.codigoRamo ?? '').trim();
        const payload: any = {
          codigoLinea,
          nombreLinea: String(row.nombreLinea).trim(),
          estadoLinea,
          codigoRamo: codigoRamo !== '' ? codigoRamo : null,
        };
        if (articuloXlistaPrecios) payload.articuloXlistaPrecios = articuloXlistaPrecios;

        try {
          await catalogosService.createLinea(payload);
          exitos++;
          existeCodigo.add(codigoLinea);
        } catch (err: any) {
          let mensajePlano = err?.response?.data?.message || err?.response?.data || err?.message || 'Error en backend';
          if (err?.response?.data?.errors) {
            const errores = err.response.data.errors; let detalle = '';
            for (let campo in errores) { if (Array.isArray(errores[campo])) { detalle += `${campo}: ${errores[campo].join('; ')}\n`; } }
            enqueueSnackbar('❌ Error en los datos: ' + detalle, { variant: 'error', autoHideDuration: 12000 }); fallos++; continue;
          }
          if (typeof mensajePlano !== 'string') { try { mensajePlano = JSON.stringify(mensajePlano); } catch {} }
          enqueueSnackbar(`❌ Fila ${i+2}: Error al guardar: ${String(mensajePlano).slice(0,120)}`, { variant: 'error', autoHideDuration: 9000 }); fallos++;
        }
      }
      enqueueSnackbar(`Importación de líneas completada. Éxitos: ${exitos}, Fallos: ${fallos}`, { variant: fallos > 0 ? 'error' : 'success', autoHideDuration: 4500 });
      loadLineas(page, rowsPerPage, searchTerm, selectedRamo);
      setTimeout(() => { setOpenImportar(false); }, 800);
    } catch (error) {
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Líneas
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventario/lineas/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                Nueva Línea
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<AttachFileIcon />} 
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Importar líneas
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }} 
              fullWidth={isMobile}
            >
              <InputLabel id="ramo-select-label">Filtrar por Ramo</InputLabel>
              <Select
                labelId="ramo-select-label"
                value={selectedRamo}
                onChange={handleRamoChange}
                label="Filtrar por Ramo"
              >
                <MenuItem value="">Todos</MenuItem>
                {ramos.map((ramo) => (
                  <MenuItem key={ramo.codigoRamo} value={ramo.codigoRamo}>
                    {ramo.nombreRamo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography sx={{ color: 'error.main', textAlign: 'center', py: 2 }}>{error}</Typography>
              ) : lineas.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 2 }}>No se encontraron líneas</Typography>
              ) : (
                lineas.map((linea) => {
                  const ramoInfo = ramos.find(r => r.codigoRamo.trim() === linea.codigoRamo?.trim());
                  return (
                    <Card 
                      key={linea.codigoLinea}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {linea.nombreLinea.trim()}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong> {linea.codigoLinea.trim()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Ramo:</strong> {ramoInfo ? ramoInfo.nombreRamo.trim() : ''}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Estado:</strong> {linea.estadoLinea === 'ACTIVO' ? 'Activo' : 'Inactivo'}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Tooltip title="Editar">
                              <IconButton
                                onClick={() => navigate(`/inventario/lineas/${linea.codigoLinea.trim()}`)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#1976d2' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleDelete(linea.codigoLinea.trim())}
                                size="small"
                                sx={{
                                  bgcolor: '#f44336',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  '&:hover': { bgcolor: '#d32f2f' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ramo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'error.main' }}>
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : lineas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No se encontraron líneas
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineas.map((linea) => {
                      const ramoInfo = ramos.find(r => r.codigoRamo.trim() === linea.codigoRamo?.trim());
                      
                      return (
                        <TableRow 
                          key={linea.codigoLinea}
                          sx={{
                            '&:hover': {
                              bgcolor: '#f9f9f9',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{linea.codigoLinea.trim()}</TableCell>
                          <TableCell>{linea.nombreLinea.trim()}</TableCell>
                          <TableCell>{ramoInfo ? ramoInfo.nombreRamo.trim() : ''}</TableCell>
                          <TableCell>{linea.estadoLinea === 'ACTIVO' ? 'Activo' : 'Inactivo'}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Editar">
                                <IconButton
                                  onClick={() => navigate(`/inventario/lineas/${linea.codigoLinea.trim()}`)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { 
                                      bgcolor: '#1976d2',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton
                                  onClick={() => handleDelete(linea.codigoLinea.trim())}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f44336',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { 
                                      bgcolor: '#d32f2f',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal de importación de líneas */}
      <Dialog 
        open={openImportar} 
        onClose={() => setOpenImportar(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Líneas</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel con el formato correcto para importar líneas.
              </Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar las líneas.
              </Typography>
              <label htmlFor="excel-upload-lineas">
                <input id="excel-upload-lineas" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onImportChange} />
                <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportar(false)} color="inherit">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Lineas;